<template>
  <div>
    <Dropdown
      id="job-dropdown"
      v-model="selectedJobUuid"
      labelName="Job"
      class="mb-16"
      name="job"
      :options="jobOptions"
      :helperText="getHelperTextForField('selectedJobUuid', 'Please select a job')"
      :status="formStatuses.selectedJobUuid"
      @blur="validateJobSelection"
      @update:modelValue="clearJobSelectionError"
    />
    <Dropdown
      v-if="selectedJobUuid === OTHER_JOB_VALUE"
      id="caretype-dropdown"
      v-model="selectedCareType"
      labelName="Care Type"
      class="mb-16"
      name="careType"
      :options="careTypeOptions"
      :helperText="getHelperTextForField('selectedCareType', 'Please select a care type')"
      :status="formStatuses.selectedCareType"
      @blur="validateCareType"
      @update:modelValue="clearCareTypeError"
    />
    <TextArea
      id="additional-details-text"
      v-model="details"
      helperText="Optional"
      labelName="Additional details"
      class="h-176 md:h-288 mb-40"
      @update:modelValue="clearDetailsError"
    />
  </div>
</template>

<script lang="ts">
import { mapGetters, mapActions } from 'vuex';
import TextArea from '@/components/textarea/TextArea.vue';
import Dropdown from '@/components/dropdown/Dropdown.vue';
import { FETCH_PARENT_JOBS } from '@/graphql/interviews/queries';

import {
  validateRequiredFields,
  createFormStatuses,
  validateFieldOnBlur,
  createErrorStateManager,
  createHelperTextGenerator,
  createErrorClearingManager,
  createFieldValidator,
} from '../helpers/formValidation';
import helperStatus from '@/helpers/status';
import getCareTypeOptions from '@/helpers/careTypeOptions';

import { JobOption, InterviewDetails } from '@/store/modules/interviewRequest/types';

const OTHER_JOB_VALUE = 'other';

// Cache static data
const CARE_TYPE_OPTIONS = getCareTypeOptions();

// Types
interface InterviewDetailsComponentData {
  errors: Record<string, string>;
  formStatuses: Record<string, helperStatus>;
  details: string;
  viewer: any;
  selectedJobUuid: string | null;
  selectedJobTitle: string | null;
  selectedCareType: string | null;
}

export default {
  name: 'InterviewDetails',
  components: {
    TextArea,
    Dropdown,
  },
  data(): InterviewDetailsComponentData {
    return {
      errors: {},
      formStatuses: createFormStatuses(['selectedJobUuid', 'selectedCareType']),
      details: '',
      viewer: {},
      selectedJobUuid: null,
      selectedJobTitle: null,
      selectedCareType: null,
    };
  },
  computed: {
    ...mapGetters('interviewRequest', ['sitter', 'sitterFallback', 'interviewDetails']),
    OTHER_JOB_VALUE(): string {
      return OTHER_JOB_VALUE;
    },
    careTypeOptions(): { display: string; value: string }[] {
      return CARE_TYPE_OPTIONS;
    },
    renderSitterFallback(): string {
      return this.sitterFallback;
    },
    errorStateManager() {
      return createErrorStateManager(this.formStatuses);
    },
    helperTextGenerator() {
      return createHelperTextGenerator(this.formStatuses);
    },
    errorClearingManager() {
      return createErrorClearingManager(this.formStatuses, this.errors);
    },
    fieldValidator() {
      return createFieldValidator(this.formStatuses, this.errors);
    },
    formattedJobOptions(): JobOption[] {
      if (!this.viewer?.jobs?.edges) {
        return [];
      }
      return this.viewer.jobs.edges.map(({ node }: any) => ({
        display: node.title,
        value: node.id,
        careType: node.careType,
      }));
    },
    jobOptions(): JobOption[] {
      const otherJobOption: JobOption = { display: 'Other', value: OTHER_JOB_VALUE };
      return [...this.formattedJobOptions, otherJobOption];
    },
    curSelectedJobTitle(): string | undefined {
      return this.formattedJobOptions.find((job) => job.value === this.selectedJobUuid)?.display;
    },
  },
  watch: {
    selectedJobUuid(val: string) {
      this.updateJobSelection(val);
    },
    selectedCareType() {
      this.clearCareTypeErrors();
    },
    // Watch for store data changes and reload if needed
    interviewDetails: {
      handler() {
        this.loadStoredInterviewDetails();
        this.restoreJobSelectionFromStore();
      },
      immediate: true,
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.loadStoredInterviewDetails();
      this.restoreJobSelectionFromStore();
      this.fetchParentJobs().then(() => {
        this.restoreJobSelectionAfterFetch();
      });
    });
  },
  beforeDestroy() {
    // Clean up Apollo query if component is unmounted
    if (this.$apollo && this.$apollo.queries) {
      this.$apollo.queries = {};
    }
  },
  methods: {
    ...mapActions('interviewRequest', ['saveInterviewDetails']),
    validateJobSelection() {
      validateFieldOnBlur(this.formStatuses, 'selectedJobUuid', this.selectedJobUuid, ['selectedJobUuid']);
    },
    validateCareType() {
      if (this.selectedJobUuid === OTHER_JOB_VALUE) {
        validateFieldOnBlur(this.formStatuses, 'selectedCareType', this.selectedCareType, ['selectedCareType']);
      }
    },
    getHelperTextForField(fieldName: string, customMessage: string): string {
      return this.helperTextGenerator.getHelperText(fieldName, customMessage);
    },
    fetchParentJobs(): Promise<void> {
      return this.$apollo
        .query({ query: FETCH_PARENT_JOBS })
        .then(({ data }) => {
          this.processGraphQLJobData(data);
          this.initializeJobSelectionIfNeeded();
        })
        .catch((error) => {
          this.handleGraphQLError(error);
        });
    },
    saveToStore(): void {
      const interviewDetails = this.buildInterviewDetailsPayload();
      this.saveInterviewDetails(interviewDetails);
    },
    // FormStepper validation method
    validate(): boolean {
      this.errorClearingManager.clearFormErrors();
      this.validateJobSelection();
      this.validateCareType();
      this.validateOtherJobRequirements();
      return this.checkFormStatusErrors();
    },

    // Data Loading & Initialization Helpers
    loadStoredInterviewDetails(): void {
      // Only load if we have valid store data
      if (!this.interviewDetails || Object.values(this.interviewDetails).every((val) => val === null || val === undefined)) {
        return;
      }

      this.details = this.interviewDetails.details || '';
    },

    restoreJobSelectionFromStore(): void {
      if (this.interviewDetails.jobUuid) {
        this.selectedJobUuid = this.interviewDetails.jobUuid;
        this.selectedJobTitle = this.interviewDetails.jobTitle;
        this.selectedCareType = this.interviewDetails.careType;
      }
    },

    restoreJobSelectionAfterFetch(): void {
      if (this.interviewDetails.jobUuid) {
        this.selectedJobUuid = this.interviewDetails.jobUuid;
        this.selectedJobTitle = this.interviewDetails.jobTitle;
        this.selectedCareType = this.interviewDetails.careType;
      }
    },

    // Job Selection Logic Helpers
    updateJobSelection(selectedJobUuid: string): void {
      this.setJobTitleFromSelection(selectedJobUuid);
      this.setCareTypeFromJob(selectedJobUuid);
      this.clearJobSelectionErrors();
    },

    setJobTitleFromSelection(selectedJobUuid: string): void {
      const curJob = this.formattedJobOptions.find((job) => job.value === selectedJobUuid);
      this.selectedJobTitle = curJob?.display || 'Other';
    },

    setCareTypeFromJob(selectedJobUuid: string): void {
      const curJob = this.formattedJobOptions.find((job) => job.value === selectedJobUuid);
      const careTypeFromJob = curJob?.careType;
      const defaultFirstCareType = this.careTypeOptions[0].value;
      this.selectedCareType = careTypeFromJob || defaultFirstCareType;
    },

    clearJobSelectionErrors(): void {
      this.errorStateManager.resetErrorState('selectedJobUuid');
    },

    clearCareTypeErrors(): void {
      this.errorStateManager.resetErrorState('selectedCareType');
    },

    // Error clearing on input
    clearJobSelectionError(): void {
      this.errorClearingManager.clearFieldError('selectedJobUuid');
    },

    clearCareTypeError(): void {
      this.errorClearingManager.clearFieldError('selectedCareType');
    },

    clearDetailsError(): void {
      this.errorClearingManager.clearFieldError('details');
    },

    // Data Processing Helpers
    processGraphQLJobData(data: any): void {
      this.viewer = data.viewer;
    },

    initializeJobSelectionIfNeeded(): void {
      if (!this.selectedJobUuid) {
        const firstJob = this.viewer?.jobs?.edges[0]?.node?.id;
        this.selectedJobUuid = firstJob || OTHER_JOB_VALUE;
      }
    },

    buildInterviewDetailsPayload(): InterviewDetails {
      const jobUuid = this.selectedJobUuid === OTHER_JOB_VALUE ? '' : this.selectedJobUuid;
      return {
        jobTitle: this.selectedJobTitle,
        jobUuid,
        careType: this.selectedCareType,
        details: this.details,
      };
    },

    // Validation Helpers
    validateOtherJobRequirements(): void {
      if (this.selectedJobUuid === OTHER_JOB_VALUE && !this.selectedCareType) {
        this.errors.careType = 'Please select a care type';
      }
    },

    checkFormStatusErrors(): boolean {
      const requiredFields = ['selectedJobUuid'];
      const fieldValues = {
        selectedJobUuid: this.selectedJobUuid,
      };

      const requiredValidation = validateRequiredFields(fieldValues, requiredFields);
      Object.assign(this.errors, requiredValidation.errors);

      const hasFormStatusErrors = Object.values(this.formStatuses).includes(helperStatus.Error);
      const hasErrors = Object.keys(this.errors).length > 0;

      return !hasFormStatusErrors && !hasErrors;
    },

    // Error Handling Helpers
    handleGraphQLError(error: any): void {
      console.error(error);
    },
  },
};
</script>
